/*
 * SHLC mix and match meter.
 *
 * Drives the discount progress bar wherever it appears. On collection pages
 * it has to do everything, because that HTML is edge cached and cannot carry
 * cart state. On the cart page Liquid has already filled the bar in and this
 * only keeps it honest after an in-page change.
 *
 * Three rules it holds to:
 *   1. Never state a percentage it cannot see in the cart totals. The count
 *      says what should happen, the money says what did, and they come apart
 *      whenever a discount is paused or another offer wins the line.
 *   2. Never leave the bar worse than the markup it started from, so every
 *      failure path just returns.
 *   3. Never change what a cart request does. It watches them, nothing more.
 */
(function () {
  'use strict';

  var IDS_KEY = 'shlcMixMatchLessonIds';
  var CART_PATHS = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;
  var roots = [];

  function money(cents) {
    return '£' + (cents / 100).toFixed(2);
  }

  function plural(n, word) {
    return n + ' ' + word + (n === 1 ? '' : 's');
  }

  function getJSON(url) {
    return fetch(url, { credentials: 'same-origin', headers: { Accept: 'application/json' } }).then(
      function (r) {
        if (!r.ok) throw new Error(url + ' returned ' + r.status);
        return r.json();
      }
    );
  }

  /* The set of products the discounts actually apply to. Read once per
     session from the collection the discounts are scoped to, so the two
     can never drift apart, and cached because it changes about monthly. */
  function lessonIds(source) {
    var cached = null;
    try {
      cached = JSON.parse(sessionStorage.getItem(IDS_KEY) || 'null');
    } catch (e) {
      cached = null;
    }
    if (cached && cached.length) return Promise.resolve(cached);

    return getJSON(source).then(function (data) {
      var ids = (data.products || []).map(function (p) {
        return p.id;
      });
      try {
        sessionStorage.setItem(IDS_KEY, JSON.stringify(ids));
      } catch (e) {
        /* private browsing, just go without the cache */
      }
      return ids;
    });
  }

  function parseTiers(raw) {
    return (raw || '')
      .split('|')
      .map(function (part) {
        var bits = part.split(',');
        return { need: parseInt(bits[0], 10), pct: parseInt(bits[1], 10) };
      })
      .filter(function (t) {
        return t.need > 0;
      });
  }

  /* Short lines. A shopper reads this out of the corner of their eye while
     they are looking at lessons, so it is one fact and one next step. */
  function wording(count, saved, savedPct, tiers) {
    var first = tiers[0];
    var hit = null;
    var next = null;

    tiers.forEach(function (t) {
      if (count >= t.need) hit = t;
      else if (!next) next = t;
    });

    if (count === 0) {
      return {
        head: 'Pick any ' + first.need + ' lessons and save ' + first.pct + '%',
        sub: 'The more lessons you add, the bigger the discount. No code needed.',
        next: first,
      };
    }

    var head =
      savedPct > 0
        ? "You're saving " + savedPct + "%, that's " + money(saved)
        : plural(count, 'lesson') + ' in your basket';

    /* The next step is worded as the extra on top of what they already have,
       not as the new total. "Save an extra 5%" is a smaller ask to picture
       than "reach 15%", and it is the same move either way.

       The extra is worked out from the ladder rather than typed, so it stays
       right if the tiers are ever re-spaced. */
    var current = hit ? hit.pct : 0;
    var extra = next ? next.pct - current : 0;

    var sub = next
      ? 'Add ' +
        plural(next.need - count, 'more lesson') +
        (current > 0 ? ' to save an extra ' : ' to save ') +
        extra +
        '%'
      : "That's the biggest discount we do";

    return { head: head, sub: sub, next: next };
  }

  /* Once the shopper has scrolled past the bar, it leaves the flow and rides
     along under the header so the next tier stays in view. Only worth doing
     when they have actually started a basket. */
  function setFollowing(root, on) {
    var spacer = root.parentNode.querySelector('[data-mm-spacer]');
    var already = root.classList.contains('is-following');
    if (on === already) return;

    if (on) {
      if (spacer) spacer.style.height = root.getBoundingClientRect().height + 'px';
      root.classList.add('is-following');
    } else {
      root.classList.remove('is-following');
      if (spacer) spacer.style.height = '';
    }
  }

  function headerOffset() {
    var header = document.querySelector('.section-header');
    if (!header) return 0;
    if (getComputedStyle(header).position !== 'sticky') return 0;
    return Math.round(header.getBoundingClientRect().height);
  }

  function updateFollow(root) {
    if (root.getAttribute('data-mm-follows') !== '1') return;
    if (!root.__mmCount) {
      setFollowing(root, false);
      return;
    }

    var offset = headerOffset();
    root.style.setProperty('--mmb-top', offset + 'px');

    /* Measured while in flow, so it stays right after a resize or a font
       swap. While following, the spacer is standing in the same place. */
    var anchor = root.classList.contains('is-following')
      ? root.parentNode.querySelector('[data-mm-spacer]')
      : root;
    if (!anchor) return;

    var top = anchor.getBoundingClientRect().top;
    if (!root.classList.contains('is-following')) {
      setFollowing(root, top < offset);
    } else if (top > offset + 2) {
      setFollowing(root, false);
    }
  }

  function render(root, count, saved, savedPct) {
    var tiers = parseTiers(root.getAttribute('data-mm-tiers'));
    if (!tiers.length) return;

    var top = tiers[tiers.length - 1].need;
    var text = wording(count, saved, savedPct, tiers);

    var head = root.querySelector('[data-mm-head]');
    var sub = root.querySelector('[data-mm-sub]');
    if (head) head.textContent = text.head;
    if (sub) sub.textContent = text.sub;

    var fill = root.querySelector('[data-mm-fill]');
    if (fill) fill.style.width = (Math.min(count, top) / top) * 100 + '%';

    root.querySelectorAll('[data-mm-mark]').forEach(function (mark) {
      var need = parseInt(mark.getAttribute('data-mm-need'), 10);
      mark.classList.toggle('is-hit', count >= need);
      mark.classList.toggle('is-next', !!text.next && text.next.need === need);
    });

    root.__mmCount = count;
    updateFollow(root);
  }

  function update(root) {
    var source = root.getAttribute('data-mm-source');
    if (!source) return;

    Promise.all([lessonIds(source), getJSON(window.Shopify.routes.root + 'cart.js')])
      .then(function (both) {
        var ids = both[0];
        var cart = both[1];
        var count = 0;
        var full = 0;
        var saved = 0;

        (cart.items || []).forEach(function (item) {
          if (ids.indexOf(item.product_id) === -1) return;
          count += item.quantity;
          full += item.original_line_price;
          saved += item.original_line_price - item.final_line_price;
        });

        var savedPct = saved > 0 && full > 0 ? Math.round((saved / full) * 100) : 0;
        render(root, count, saved, savedPct);
      })
      .catch(function (err) {
        /* Leave the bar exactly as rendered, but say so. A silent catch here
           once hid a ReferenceError for a whole round of testing: the bar sat
           on its empty state and looked like a caching problem. */
        if (window.console && console.warn) console.warn('[shlc mixmatch]', err);
      });
  }

  function updateAll() {
    roots.forEach(update);
  }

  /* Watch cart requests rather than guess at which button made them, so a
     quick add, the cart page and any app all move the bar the same way.
     The original fetch is called untouched and its result handed straight
     back; this only listens for it finishing. */
  function watchCartRequests() {
    if (!window.fetch || window.fetch.__shlcMixMatch) return;

    var original = window.fetch;
    var patched = function (input, init) {
      var url = typeof input === 'string' ? input : input && input.url ? input.url : '';
      var result = original.apply(this, arguments);
      if (url && CART_PATHS.test(url)) {
        result.then(function () {
          updateAll();
        }, function () {});
      }
      return result;
    };
    patched.__shlcMixMatch = true;
    window.fetch = patched;
  }

  function init() {
    roots = Array.prototype.slice.call(document.querySelectorAll('[data-shlc-mixmatch]'));
    if (!roots.length) return;

    updateAll();
    watchCartRequests();

    /* Dawn announces its own cart changes, which covers anything that
       changes the cart without going through fetch. */
    if (typeof window.subscribe === 'function') {
      try {
        window.subscribe('cart-update', updateAll);
      } catch (e) {
        /* older pubsub, the fetch watcher still covers it */
      }
    }

    /* Coming back from a product page is usually a bfcache restore, which
       fires no load event, so the bar would show a stale count. */
    window.addEventListener('pageshow', function (e) {
      if (e.persisted) updateAll();
    });

    document.addEventListener('visibilitychange', function () {
      if (!document.hidden) updateAll();
    });

    var pending = false;
    var onScroll = function () {
      if (pending) return;
      pending = true;
      setTimeout(function () {
        pending = false;
        roots.forEach(updateFollow);
      }, 60);
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
